import React from 'react'
import type { SvgIconProps } from '@mui/material'
import {
  TextFields,
  Title,
  HorizontalRule,
  List,
  SmartButton,
  SwipeRight,
  Phone,
  ViewList,
  ExpandMore,
  AttachFile,
} from '@mui/icons-material'
import type { FormElement } from '@mobileTypes'

const ICON_COMPONENTS = {
  FIELD: TextFields,
  PHONE_NUMBER: Phone,
  TITLE: Title,
  DIVIDER: HorizontalRule,
  BUTTON: SmartButton,
  SWIPE: SwipeRight,
  EMBEDDED_LIST: List,
  LIST_ITEM: ViewList,
  ACCORDION: ExpandMore,
  FILE: AttachFile,
} as const

/** Returns icon ReactNode for a form element type; pass props (e.g. fontSize: 'small') for styling. */
export function getFormElementIcon(
  type: string,
  props?: SvgIconProps
): React.ReactNode {
  const Icon = ICON_COMPONENTS[type as keyof typeof ICON_COMPONENTS]
  return Icon ? React.createElement(Icon, props) : null
}

/** Default-size icons for form element types (e.g. AvailableElementsPanel). */
export const FORM_ELEMENT_ICONS: Record<string, React.ReactNode> =
  Object.fromEntries(
    Object.entries(ICON_COMPONENTS).map(([k, v]) => [
      k,
      React.createElement(v, {}),
    ])
  )

/** Short summary string for a form element (e.g. list/tree labels). */
export function getFormElementSummary(element: FormElement): string {
  switch (element.type) {
    case 'FIELD':
      return element.data?.title || element.title || 'Untitled Field'
    case 'PHONE_NUMBER':
      return element.title || element.internalId || 'Phone Number'
    case 'TITLE':
      return element.title || 'Untitled Title'
    case 'BUTTON':
      return (
        element.title ||
        element.command?.name ||
        (element.actions?.length ? `${element.actions.length} actions` : null) ||
        'Untitled Button'
      )
    case 'DIVIDER':
      return 'Divider'
    case 'SWIPE':
      return (
        element.title ||
        element.command?.name ||
        (element.actions?.length ? `${element.actions.length} actions` : null) ||
        'Swipe Action'
      )
    case 'EMBEDDED_LIST':
      return element.listHeader || element.embeddedListItemId || 'Embedded List'
    case 'LIST_ITEM':
      return element.listItemConfigId || 'List Item'
    case 'ACCORDION':
      return element.title || 'Accordion'
    case 'FILE':
      return element.title || 'File'
    default:
      return 'Unknown'
  }
}
